import React, { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Briefcase, Banknote, Calendar, Building2, Clock, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { useI18n } from "../lib/i18n";
import { API } from "../lib/auth";
import ApplyDialog from "../components/ApplyDialog";
import { useSeo, SITE_URL } from "../lib/useSeo";
import { CITIES } from "../lib/seoTaxonomy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Legacy URL handler: if someone (Google index, old backlinks, social shares) hits
// /jobs/in-<region-slug>, we resolve the closest known city and redirect to /jobs/location/<slug>.
const normalise = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
const resolveLegacyInSlug = (id) => {
  if (!id || typeof id !== "string" || !id.toLowerCase().startsWith("in-")) return null;
  const candidate = normalise(decodeURIComponent(id.slice(3)));
  if (!candidate) return null;
  const known = CITIES.find((c) => c.slug === candidate)
    || CITIES.find((c) => (c.aliases || []).some((a) => normalise(a) === candidate))
    || CITIES.find((c) => (c.aliases || []).some((a) => normalise(a).includes(candidate) || candidate.includes(normalise(a))));
  return known ? `/jobs/location/${known.slug}` : "/jobs/locations";
};

export default function JobDetailPage() {
  const params = useParams();
  const { t } = useI18n();
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);

  // Intercept legacy /jobs/in-... URLs BEFORE making any network calls.
  const legacyRedirect = (!params.slugPrefix && params.id) ? resolveLegacyInSlug(params.id) : null;

  const fetchJob = async () => {
    if (legacyRedirect) return; // don't fetch when we're about to redirect
    try {
      let r;
      if (params.slugPrefix && params.slugSuffix) {
        r = await axios.get(`${API}/jobs/by-slug/${params.slugPrefix}/${params.slugSuffix}`);
      } else {
        r = await axios.get(`${API}/jobs/${params.id}`);
      }
      setJob(r.data);
    } catch (e) {
      setJob(null);
    }
  };

  useEffect(() => { fetchJob(); /* eslint-disable-next-line */ }, [params.id, params.slugPrefix, params.slugSuffix]);

  // Build SEO meta + canonical for THIS job. Called BEFORE any early return to obey the Rules of Hooks.
  const cityShort = job?.location ? job.location.split(",")[0].trim() : "Mauritius";
  const seoTitle = job ? `${job.title}${job.location ? ` — ${cityShort}` : ""} | Motravay` : "Job — Motravay";
  const seoDescription = job
    ? ((job.description || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 200) || `Apply for ${job.title} at ${job.company_name} in ${job.location || "Mauritius"}.`)
    : "Job listing on Motravay — the #1 job board in Mauritius.";
  const slugPath = job ? (job.slug ? `/jobs/${job.slug}` : `/jobs/${job.id}`) : (window?.location?.pathname || "/jobs");
  const hasExternalUrl = !!(job && job.application_url && /^https?:\/\//i.test(job.application_url));
  const structured = job ? {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "hiringOrganization": { "@type": "Organization", "name": job.company_name },
    "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressLocality": job.location, "addressCountry": "MU" } },
    "employmentType": job.contract_type,
    "datePosted": job.created_at,
    "validThrough": job.expires_at,
    "baseSalary": job.salary_min ? { "@type": "MonetaryAmount", "currency": "MUR", "value": { "@type": "QuantitativeValue", "minValue": job.salary_min, "maxValue": job.salary_max, "unitText": "MONTH" } } : undefined,
    ...(hasExternalUrl ? { "directApply": false, "url": job.application_url } : {}),
  } : null;
  useSeo({ title: seoTitle, description: seoDescription, canonical: slugPath, type: "article", jsonLd: structured });

  // Legacy URL redirect (must come AFTER all hooks to obey Rules of Hooks)
  if (legacyRedirect) return <Navigate to={legacyRedirect} replace />;

  if (job === null) return <div className="p-12 text-center text-slate-500">Loading…</div>;

  const fmt = (n) => n ? new Intl.NumberFormat("en-MU").format(n) : "";
  const logoUrl = job.company_logo_url ? (job.company_logo_url.startsWith("http") ? job.company_logo_url : `${BACKEND_URL}${job.company_logo_url}`) : null;
  const expiresOn = job.expires_at ? new Date(job.expires_at).toLocaleDateString() : null;

  // Fire-and-forget click tracking (must NOT block external navigation,
  // otherwise mobile Chrome treats window.open as out-of-gesture and blocks it).
  const trackClick = () => {
    try { axios.post(`${API}/jobs/${job.id}/click`).catch(() => {}); } catch (_) { /* ignore */ }
  };

  const ApplyCta = ({ testid }) => hasExternalUrl ? (
    // Use a real anchor so the browser navigates natively (no popup blocker on mobile Chrome / Safari).
    <a
      href={job.application_url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={trackClick}
      data-testid={testid}
      className="inline-flex items-center justify-center bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full h-12 px-8 text-base font-semibold transition-colors"
    >
      {t("jobs.apply")} <ExternalLink className="h-4 w-4 ml-2" />
    </a>
  ) : (
    <Button onClick={() => setOpen(true)} data-testid={testid} className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full h-12 px-8 text-base font-semibold">
      {t("jobs.apply")}
    </Button>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="job-detail">
      <article className="bg-white border border-slate-200 rounded-2xl p-8">
        <header className="flex flex-col md:flex-row justify-between gap-4 mb-6 pb-6 border-b border-slate-100">
          <div className="flex gap-4 min-w-0">
            <div className="h-14 w-14 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
              {logoUrl ? <img src={logoUrl} alt={`${job.company_name} logo`} className="w-full h-full object-contain p-1" /> : <Building2 className="h-6 w-6 text-slate-400" />}
            </div>
            <div className="min-w-0">
              <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">{job.sector || "—"}</span>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">{job.title}</h1>
              {(job.employer_slug || job.employer_id) ? (
                <Link to={`/career/${job.employer_slug || job.employer_id}`} data-testid="job-detail-company" className="text-slate-600 mt-1 inline-block hover:text-[#1A8FE3] hover:underline">{job.company_name}</Link>
              ) : (
                <p className="text-slate-600 mt-1">{job.company_name}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" />{job.location}</span>
                <span className="inline-flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-slate-400" />{job.contract_type}</span>
                {job.salary_min && (
                  <span className="inline-flex items-center gap-1.5"><Banknote className="h-4 w-4 text-slate-400" />Rs {fmt(job.salary_min)}{job.salary_max ? ` - ${fmt(job.salary_max)}` : ""}</span>
                )}
                <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-slate-400" />{t("jobs.postedOn")} {new Date(job.created_at).toLocaleDateString()}</span>
                {expiresOn && <span className="inline-flex items-center gap-1.5 text-amber-700"><Clock className="h-4 w-4" />Valid until {expiresOn}</span>}
                {hasExternalUrl && <span className="inline-flex items-center gap-1.5 text-[#1A8FE3]"><ExternalLink className="h-4 w-4" />Apply on partner site</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <ApplyCta testid="apply-button" />
          </div>
        </header>

        <section className="prose prose-slate max-w-none">
          <h2 className="font-display text-xl font-semibold text-slate-900">Description</h2>
          <p className="whitespace-pre-line text-slate-700 leading-relaxed">{job.description}</p>
          {job.requirements && (
            <>
              <h2 className="font-display text-xl font-semibold text-slate-900 mt-6">Requirements</h2>
              <p className="whitespace-pre-line text-slate-700 leading-relaxed">{job.requirements}</p>
            </>
          )}
          {job.benefits && (
            <>
              <h2 className="font-display text-xl font-semibold text-slate-900 mt-6">Benefits</h2>
              <p className="whitespace-pre-line text-slate-700 leading-relaxed">{job.benefits}</p>
            </>
          )}
        </section>

        <div className="mt-8 pt-6 border-t border-slate-100">
          <ApplyCta testid="apply-button-bottom" />
        </div>
      </article>
      {!hasExternalUrl && <ApplyDialog job={job} open={open} onOpenChange={setOpen} onSuccess={fetchJob} />}
    </div>
  );
}
